import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import TestInterface from "@/components/tests/TestInterface";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Clock, User } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Seo from "@/components/seo/Seo";

interface Question {
  id: string;
  text: string;
  options: Array<{
    id: string;
    text: string;
    is_correct: boolean;
  }>;
  explanation?: string;
}

interface TestInvitation {
  id: string;
  test_id: string;
  email: string;
  completed_at: string | null;
  score: number | null;
}

interface TestData {
  title: string;
  description: string | null;
  subject: string;
  grade: string;
  topic: string;
  time_limit: number | null;
  total_questions: number;
}

const InvitedTestPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Get test data from navigation state
  const stateData = location.state as { invitation?: TestInvitation; testData?: TestData };
  const testId = stateData?.invitation?.test_id;
  
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<TestInvitation | null>(stateData?.invitation || null);
  const [testData, setTestData] = useState<TestData | null>(stateData?.testData || null);

  useEffect(() => {
    if (!testId || !user?.email) {
      toast.error('Invalid test access');
      navigate('/student/dashboard');
      return;
    }
    
    if (stateData?.invitation && stateData?.testData) {
      setInvitation(stateData.invitation);
      setTestData(stateData.testData);
      fetchQuestions();
    } else {
      // If no state data, redirect back to dashboard
      toast.error('Invalid test access. Please use the invitation link.');
      navigate('/student/dashboard');
    }
  }, [testId, user]);

// Remove unused functions since we no longer fetch data by testId from URL
  
  const fetchQuestions = async () => {
    try {
      // Fetch questions from instructor_test_questions for this test
      const { data: testQuestions, error: questionsError } = await supabase
        .from('instructor_test_questions')
        .select(`
          id,
          question_order,
          points,
          custom_question_data,
          question_id,
          questions (
            id,
            text,
            explanation,
            options (
              id,
              text,
              is_correct
            )
          )
        `)
        .eq('test_id', testId)
        .order('question_order');

      if (questionsError) throw questionsError;

      // Transform the data into the expected format
      const transformedQuestions: Question[] = testQuestions
        .map((tq) => {
          if (tq.questions) {
            // Use database question
              return {
                id: `q_${tq.question_order}`,
                text: tq.questions.text,
                explanation: tq.questions.explanation || undefined,
                options: (tq.questions.options || []).map((opt, index) => ({
                  ...opt,
                  id: `q_${tq.question_order}_opt_${index}`
                }))
              };
          } else if (tq.custom_question_data && typeof tq.custom_question_data === 'object') {
            // Use custom question data
            const customData = tq.custom_question_data as any;
            
            // Handle different question types
            if (customData.type === 'multiple_choice') {
              return {
                id: `q_${tq.question_order}`,
                text: customData.text,
                explanation: customData.explanation,
                options: customData.options.map((opt: any, index: number) => ({
                  id: `q_${tq.question_order}_opt_${index}`,
                  text: opt.text,
                  is_correct: opt.is_correct
                }))
              };
            } else if (customData.type === 'true_false') {
              return {
                id: `q_${tq.question_order}`,
                text: customData.text,
                explanation: customData.explanation,
                options: [
                  {
                    id: `q_${tq.question_order}_true`,
                    text: 'True',
                    is_correct: customData.correct_answer === true
                  },
                  {
                    id: `q_${tq.question_order}_false`,
                    text: 'False',
                    is_correct: customData.correct_answer === false
                  }
                ]
              };
            } else if (customData.type === 'matching') {
              // Convert matching to multiple choice by asking about each pair
              const pairs = customData.pairs || [];
              if (pairs.length > 0) {
                const firstPair = pairs[0];
                return {
                  id: `q_${tq.question_order}`,
                  text: `${customData.text}\nWhat is the decimal equivalent of ${firstPair.left}?`,
                  explanation: customData.explanation,
                  options: pairs.map((pair: any, index: number) => ({
                    id: `q_${tq.question_order}_pair_${index}`,
                    text: pair.right,
                    is_correct: index === 0 // First pair is correct for this question
                  }))
                };
              }
            } else if (customData.type === 'fill_blank') {
              const correctAnswer = customData.blanks?.[0] || '';
              const incorrectOptions = ['0.25', '0.5', '0.75'].filter(opt => opt !== correctAnswer);
              
              return {
                id: `q_${tq.question_order}`,
                text: customData.text.replace('_____', '[BLANK]'),
                explanation: customData.explanation,
                options: [
                  {
                    id: `q_${tq.question_order}_correct`,
                    text: correctAnswer,
                    is_correct: true
                  },
                  ...incorrectOptions.slice(0, 3).map((opt, index) => ({
                    id: `q_${tq.question_order}_wrong_${index}`,
                    text: opt,
                    is_correct: false
                  }))
                ]
              };
            } else if (customData.type === 'puzzle') {
              const solution = customData.puzzle_data?.solution || '';
              const scrambled = customData.puzzle_data?.content || '';
              
              return {
                id: `q_${tq.question_order}`,
                text: `${customData.text}`,
                explanation: customData.explanation,
                options: [
                  {
                    id: `q_${tq.question_order}_correct`,
                    text: solution,
                    is_correct: true
                  },
                  {
                    id: `q_${tq.question_order}_wrong_1`,
                    text: 'DECIMAL',
                    is_correct: false
                  },
                  {
                    id: `q_${tq.question_order}_wrong_2`,
                    text: 'NUMBER',
                    is_correct: false
                  },
                  {
                    id: `q_${tq.question_order}_wrong_3`,
                    text: 'PERCENT',
                    is_correct: false
                  }
                ]
              };
            }
            
            return null;
          }
          return null;
        })
        .filter((q) => q !== null && q.text && q.options && q.options.length > 0) as Question[];

      setQuestions(transformedQuestions);
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to load test questions');
    } finally {
      setLoading(false);
    }
  };

  const handleTestSubmit = async (answers: Array<{ questionId: string; selectedOptionId: string | null; isCorrect: boolean }>) => {
    if (!invitation || !testData) return;

    try {
      // Calculate score
      const correctAnswers = answers.filter(a => a.isCorrect).length;
      const totalQuestions = answers.length;
      const score = Math.round((correctAnswers / totalQuestions) * 100);

      // Update the invitation with completion data
      const { error: updateError } = await supabase
        .from('test_invitations')
        .update({
          completed_at: new Date().toISOString(),
          score: score
        })
        .eq('id', invitation.id);

      if (updateError) throw updateError;

      toast.success(`Test completed! Your score: ${score}%`);
      
      // Navigate to results or back to dashboard
      navigate('/student/dashboard', {
        state: { 
          testCompleted: true,
          score: score,
          testTitle: testData.title
        }
      });
    } catch (error) {
      console.error('Error submitting test:', error);
      toast.error('Failed to submit test');
    }
  };

  if (loading) {
    return (
      <>
        <Seo title="Loading test" description="Loading your invited test." canonicalPath="/invited-test" noindex />
        <div className="min-h-screen flex flex-col">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p>Loading {testData?.title || 'test'}...</p>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  if (!invitation || !testData) {
    return (
      <>
        <div className="min-h-screen flex flex-col">
          <Header />
          <main className="flex-1">
            <div className="container mx-auto px-4 py-8">
              <Card>
                <CardHeader>
                  <CardTitle>Access Denied</CardTitle>
                  <CardDescription>
                    You don't have permission to access this test.
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  if (invitation.completed_at) {
    return (
      <>
        <div className="min-h-screen flex flex-col">
          <Header />
          <main className="flex-1">
            <div className="container mx-auto px-4 py-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    {testData.title}
                  </CardTitle>
                  <CardDescription>
                    You have already completed this test
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <BookOpen className="h-4 w-4" />
                      {testData.subject} - Grade {testData.grade}
                    </div>
                    {testData.time_limit && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {testData.time_limit} minutes
                      </div>
                    )}
                  </div>
                  
                  {invitation.score !== null && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h3 className="font-semibold text-green-800">Your Score</h3>
                      <p className="text-2xl font-bold text-green-600">{invitation.score}%</p>
                    </div>
                  )}
                  
                </CardContent>
              </Card>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  if (questions.length === 0) {
    return (
      <>
        <div className="min-h-screen flex flex-col">
          <Header />
          <main className="flex-1">
            <div className="container mx-auto px-4 py-8">
              <Card>
                <CardHeader>
                  <CardTitle>No Questions Available</CardTitle>
                  <CardDescription>
                    This test doesn't have any questions yet.
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
          <div className="container mx-auto px-4 py-8">
            <div className="mb-6">
              <Card className="border-l-4 border-l-primary">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <BookOpen className="h-5 w-5" />
                        {testData.title}
                      </CardTitle>
                      <CardDescription>
                        {testData.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                   <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                     <div className="flex items-center gap-1">
                       <BookOpen className="h-4 w-4" />
                       {testData.subject} - Grade {testData.grade}
                     </div>
                     {testData.time_limit && (
                       <div className="flex items-center gap-1">
                         <Clock className="h-4 w-4" />
                         Time Limit: {testData.time_limit} minutes
                       </div>
                     )}
                     <div className="flex items-center gap-1">
                       <User className="h-4 w-4" />
                       Questions: {questions.length}
                     </div>
                   </div>
                </CardContent>
              </Card>
            </div>

            <TestInterface
              questions={questions}
              onSubmit={handleTestSubmit}
              subjectName={testData.subject}
              testMode="exam"
            />
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default InvitedTestPage;